import React, { useEffect } from "react";
import { createPortal } from "react-dom";


export interface SidePanelProps {
  /** Open/close state */
  isOpen: boolean;
  /** Close handler (overlay click, ESC, X 버튼 등에서 호출) */
  onClose: () => void;
  /** 헤더에 표시할 제목(문자/노드 모두 가능) */
  title?: React.ReactNode;
  /** 패널 너비. 예: 600, "28rem", "w-[600px]" */
  width?: number | string;
  /** 오른쪽/왼쪽 표시 */
  side?: "right" | "left";
  /** 헤더 우측에 표시할 액션(버튼 등) */
  headerActions?: React.ReactNode;
  /** 푸터에 들어갈 노드(버튼 영역) */
  footer?: React.ReactNode;
  /** body 스크롤 허용 여부. 기본 true */
  scrollable?: boolean;
  /** 오버레이 클릭으로 닫기 허용 */
  closeOnOverlay?: boolean;
  /** 추가 클래스 */
  className?: string;
  /** 컨텐츠 */
  children?: React.ReactNode;
}

/**
 * 재사용 가능한 사이드패널 컴포넌트.
 * - TailwindCSS 기반 애니메이션/레이아웃
 * - ESC/오버레이 클릭 닫기 지원
 * - 좌/우 방향, 가변 너비, 헤더/푸터 슬롯 제공
 */
const SidePanel: React.FC<SidePanelProps> = ({
  isOpen,
  onClose,
  title,
  width = 600,
  side = "right",
  headerActions,
  footer,
  scrollable = true,
  closeOnOverlay = true,
  className = "",
  children,
}) => {
  // ESC로 닫기
  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [isOpen, onClose]);

  // 바디 스크롤 잠금 (열렸을 때)
  useEffect(() => {
    if (isOpen) {
      const prev = document.body.style.overflow;
      document.body.style.overflow = "hidden";
      return () => {
        document.body.style.overflow = prev;
      };
    }
  }, [isOpen]);

  // width 처리: number → px, 일반 CSS 문자열 → style, Tailwind 클래스는 className으로만
  const widthStyle: React.CSSProperties | undefined =
    typeof width === "number"
      ? { width: `${width}px` }
      : typeof width === "string" &&
        !(width.startsWith("w-") || width.includes("["))
        ? { width }
        : undefined;

  const sidePlacement = side === "right" ? "right-0" : "left-0";
  const translateClass = isOpen
    ? "translate-x-0"
    : side === "right"
      ? "translate-x-full"
      : "-translate-x-full";

  const panelClasses = [
    "fixed top-0 h-full bg-white shadow-lg z-50 transform transition-transform duration-300 ease-in-out",
    sidePlacement,
    typeof width === "string" && (width.includes("w-") || width.includes("["))
      ? width
      : "",
    translateClass,
    className,
  ]
    .filter(Boolean)
    .join(" ");

  return createPortal(
    <>
      {/* Overlay */}
      <div
        aria-hidden
        className={`fixed inset-0 z-40 bg-black/30 backdrop-blur-[1px] transition-opacity duration-300
      ${isOpen ? "opacity-100" : "opacity-0 pointer-events-none"}
    `}
        onClick={() => closeOnOverlay && onClose()}
      />

      {/* Panel */}
      <aside
        role="dialog"
        aria-modal="true"
        aria-label={typeof title === "string" ? title : undefined}
        className={panelClasses}
        style={widthStyle}
      >
        <div className="flex flex-col h-full">
          {/* Header */}
          {(title || headerActions) && (
            <div className="flex items-center justify-between px-6 py-4 border-b">
              <div className="min-w-0 pr-3">
                {title && (
                  <h3 className="text-lg font-bold text-gray-800 truncate">
                    {title}
                  </h3>
                )}
              </div>
              <div className="flex items-center gap-2">
                {headerActions}
                <button
                  onClick={onClose}
                  className="text-gray-400 hover:text-gray-600 text-xl"
                  aria-label="Close panel"
                >
                  ✕
                </button>
              </div>
            </div>
          )}

          {/* Body */}
          <div className={scrollable ? "flex-1 overflow-y-auto px-6 py-6" : "flex-1 px-6 py-6"}>
            {children}
          </div>

          {/* Footer */}
          {footer && <div className="px-6 py-4 border-t">{footer}</div>}
        </div>
      </aside>
    </>,
    document.body
  );
};

export default SidePanel;
